import { useEffect } from 'react';

/**
 * Sequence-in-progress guard — closes the `g`-prefix vs bare-letter collision.
 *
 * THE COLLISION
 * react-hotkeys-hook v5 attaches an INDEPENDENT `document` `keydown` listener
 * per `useHotkeys` call. A `g>x` SEQUENCE hotkey (go-to navigation) and a bare
 * single-letter action hotkey (vuln `t/o/c/a/r`, asset `f/u/d`, seed `a/r/d/f`,
 * tech `c/t/e`) therefore both live on `document`. On the SEQUENCE's terminal
 * key (e.g. `g` then `d`) BOTH listeners fire for the same event: the sequence
 * navigates AND the bare `d` action runs (e.g. opens Delete) when rows are
 * selected. `preventDefault()` only suppresses the BROWSER's default action, not
 * sibling JS listeners — only `stopImmediatePropagation()` would, and the
 * library never calls it. So the two listeners cannot suppress each other from
 * inside react-hotkeys-hook.
 *
 * THE FIX
 * A module-level flag that bare-letter actions read SYNCHRONOUSLY during the
 * same dispatch. While a sequence prefix (`g`) is "in progress" (i.e. inside the
 * library's ~1000ms sequence window), `useActionHotkey` bails before invoking
 * its handler, so the bare letter does nothing and only the sequence navigates.
 *
 * WHY MODULE SCOPE, NOT REACT STATE
 * The action listeners are independent `document` listeners owned by unrelated
 * components (sections + both table components). They must read the flag
 * synchronously while handling the SAME key event — there is no shared React
 * render or context they all observe in time. React state updates are async
 * (batched, next render) and component-scoped, so they cannot gate a sibling
 * listener firing in the current synchronous dispatch. A module-level mutable
 * flag is the correct (and simplest) cross-listener channel.
 *
 * IDEMPOTENT SINGLE LISTENER (multiple mounts)
 * `useSequencePrefixGuard` is intended to be called ONCE (in `GoToNavigation`,
 * not per-binding). It is nonetheless ref-counted so that if more than one
 * component mounts it, exactly ONE shared `document` listener and ONE shared
 * timer exist, and the listener is removed only when the last consumer unmounts.
 * This keeps the flag a single source of truth regardless of mount count and
 * avoids N redundant listeners stacking the flag.
 */

let sequenceInProgress = false;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

/** Matches react-hotkeys-hook v5's default `sequenceTimeoutMs` (1e3). */
const SEQUENCE_WINDOW_MS = 1000;

/**
 * Read synchronously inside any action listener: is a `g`-style sequence
 * currently mid-flight? Bare-letter actions should bail when this is `true` so
 * the sequence's terminal key doesn't double-fire a row/section action.
 */
export function isSequenceInProgress(): boolean {
  return sequenceInProgress;
}

function isFormField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

// Shared listener + ref-count, installed lazily on first consumer.
let consumerCount = 0;
let activePrefixes: string[] = [];
let documentListener: ((event: KeyboardEvent) => void) | null = null;

/**
 * Normalize a `KeyboardEvent.code` to the same key token react-hotkeys-hook v5
 * matches sequences against. rhk derives the sequence key from `event.code` (NOT
 * `event.key`) via `code.toLowerCase().replace(/key|digit|numpad/, '')` — e.g.
 * `'KeyG' → 'g'`, `'Digit1' → '1'`. Matching `event.key` here would diverge from
 * rhk on non-US layouts (where the physical `KeyG` may emit a different
 * `event.key`), letting the guard miss the `g` prefix so the terminal sequence
 * key double-fires the bare-letter action. Mirroring rhk's source keeps the
 * guard and the hook in lock-step on what counts as the prefix.
 */
export function codeToHotkey(code: string): string {
  return code.toLowerCase().replace(/key|digit|numpad/, '');
}

function handlePrefixKeydown(event: KeyboardEvent): void {
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (!activePrefixes.includes(codeToHotkey(event.code))) return;
  if (isFormField(event.target)) return;

  // Open (or re-open) the window. Re-pressing a prefix resets the timer so a
  // slow second key still counts as "in a sequence".
  sequenceInProgress = true;
  if (clearTimer !== null) {
    clearTimeout(clearTimer);
  }
  clearTimer = setTimeout(() => {
    sequenceInProgress = false;
    clearTimer = null;
  }, SEQUENCE_WINDOW_MS);
}

/**
 * Install a single `document` `keydown` listener that opens the
 * "sequence in progress" window whenever one of `prefixes` is pressed bare
 * (no Ctrl/Meta/Alt) outside a form field. Cleans up the listener and clears
 * any pending timer when the last consumer unmounts.
 *
 * Call ONCE in the parent that owns the sequences (`GoToNavigation`). Deriving
 * the prefixes from the bindings keeps this in lock-step with the navigation
 * keys — see `useGoToNavigation`.
 */
export function useSequencePrefixGuard(prefixes: string[]): void {
  useEffect(() => {
    // SSR / non-DOM guard.
    if (typeof document === 'undefined') return;

    activePrefixes = prefixes;
    consumerCount += 1;

    if (documentListener === null) {
      documentListener = handlePrefixKeydown;
      document.addEventListener('keydown', documentListener);
    }

    return () => {
      consumerCount -= 1;
      if (consumerCount <= 0) {
        consumerCount = 0;
        if (documentListener !== null) {
          document.removeEventListener('keydown', documentListener);
          documentListener = null;
        }
        if (clearTimer !== null) {
          clearTimeout(clearTimer);
          clearTimer = null;
        }
        sequenceInProgress = false;
        activePrefixes = [];
      }
    };
    // `prefixes` is a stable derived list (deduped from static bindings). The
    // listener reads the module-level `activePrefixes`, refreshed above on mount.
    // Keyed on the joined value so identity churn of the array doesn't re-run.
  }, [prefixes.join(',')]);
}
